import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowRight, Phone, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema, faqSchema } from '../utils/seoSchemas';
import { BLOCKS, DEFAULT_LAYOUT } from '../components/freezone/FreeZoneBlocks';

// Shared shell for free zone pages; each zone config sets its own section order via `layout`.
// Tailwind class maps keyed by accent so the JIT compiler retains them.
const ACCENT = {
  teal: { hero: 'from-slate-900 via-teal-900 to-slate-900', text: 'text-teal-400', txt: 'text-teal-600', bg: 'bg-teal-100', bg50: 'bg-teal-50', border100: 'border-teal-100', borderHover: 'hover:border-teal-500', btnPrimary: 'bg-teal-500 hover:bg-teal-600', ctaFrom: 'from-teal-700', ctaTo: 'to-teal-800', ctaText: 'text-teal-700', ctaSubText: 'text-teal-100', tile: 'bg-teal-50 border-teal-100' },
  emerald: { hero: 'from-slate-900 via-emerald-900 to-teal-900', text: 'text-emerald-400', txt: 'text-emerald-600', bg: 'bg-emerald-100', bg50: 'bg-emerald-50', border100: 'border-emerald-100', borderHover: 'hover:border-emerald-500', btnPrimary: 'bg-emerald-500 hover:bg-emerald-600', ctaFrom: 'from-emerald-700', ctaTo: 'to-teal-700', ctaText: 'text-emerald-700', ctaSubText: 'text-emerald-100', tile: 'bg-emerald-50 border-emerald-100' },
  blue: { hero: 'from-slate-900 via-indigo-900 to-blue-900', text: 'text-blue-400', txt: 'text-blue-600', bg: 'bg-blue-100', bg50: 'bg-blue-50', border100: 'border-blue-100', borderHover: 'hover:border-blue-500', btnPrimary: 'bg-blue-500 hover:bg-blue-600', ctaFrom: 'from-blue-700', ctaTo: 'to-indigo-700', ctaText: 'text-blue-700', ctaSubText: 'text-blue-100', tile: 'bg-blue-50 border-blue-100' },
  amber: { hero: 'from-slate-900 via-amber-900 to-stone-900', text: 'text-amber-400', txt: 'text-amber-600', bg: 'bg-amber-100', bg50: 'bg-amber-50', border100: 'border-amber-100', borderHover: 'hover:border-amber-500', btnPrimary: 'bg-amber-500 hover:bg-amber-600', ctaFrom: 'from-amber-600', ctaTo: 'to-yellow-700', ctaText: 'text-amber-700', ctaSubText: 'text-amber-100', tile: 'bg-amber-50 border-amber-100' },
  rose: { hero: 'from-slate-900 via-rose-900 to-pink-900', text: 'text-rose-400', txt: 'text-rose-600', bg: 'bg-rose-100', bg50: 'bg-rose-50', border100: 'border-rose-100', borderHover: 'hover:border-rose-500', btnPrimary: 'bg-rose-500 hover:bg-rose-600', ctaFrom: 'from-rose-700', ctaTo: 'to-pink-700', ctaText: 'text-rose-700', ctaSubText: 'text-rose-100', tile: 'bg-rose-50 border-rose-100' },
  orange: { hero: 'from-slate-900 via-orange-900 to-stone-900', text: 'text-orange-400', txt: 'text-orange-600', bg: 'bg-orange-100', bg50: 'bg-orange-50', border100: 'border-orange-100', borderHover: 'hover:border-orange-500', btnPrimary: 'bg-orange-500 hover:bg-orange-600', ctaFrom: 'from-orange-600', ctaTo: 'to-amber-700', ctaText: 'text-orange-700', ctaSubText: 'text-orange-100', tile: 'bg-orange-50 border-orange-100' },
  purple: { hero: 'from-slate-900 via-purple-900 to-violet-900', text: 'text-purple-400', txt: 'text-purple-600', bg: 'bg-purple-100', bg50: 'bg-purple-50', border100: 'border-purple-100', borderHover: 'hover:border-purple-500', btnPrimary: 'bg-purple-500 hover:bg-purple-600', ctaFrom: 'from-purple-700', ctaTo: 'to-violet-700', ctaText: 'text-purple-700', ctaSubText: 'text-purple-100', tile: 'bg-purple-50 border-purple-100' },
  indigo: { hero: 'from-slate-900 via-indigo-900 to-slate-900', text: 'text-indigo-400', txt: 'text-indigo-600', bg: 'bg-indigo-100', bg50: 'bg-indigo-50', border100: 'border-indigo-100', borderHover: 'hover:border-indigo-500', btnPrimary: 'bg-indigo-500 hover:bg-indigo-600', ctaFrom: 'from-indigo-700', ctaTo: 'to-blue-700', ctaText: 'text-indigo-700', ctaSubText: 'text-indigo-100', tile: 'bg-indigo-50 border-indigo-100' },
  green: { hero: 'from-slate-900 via-green-900 to-emerald-900', text: 'text-green-400', txt: 'text-green-600', bg: 'bg-green-100', bg50: 'bg-green-50', border100: 'border-green-100', borderHover: 'hover:border-green-500', btnPrimary: 'bg-green-500 hover:bg-green-600', ctaFrom: 'from-green-700', ctaTo: 'to-emerald-700', ctaText: 'text-green-700', ctaSubText: 'text-green-100', tile: 'bg-green-50 border-green-100' }
};

const FreeZoneAuthorityPage = ({ zone }) => {
  usePageSeo({
    title: zone.metaTitle,
    description: zone.metaDescription,
    path: zone.slug
  });

  const a = ACCENT[zone.accent] || ACCENT.teal;
  const testIdBase = zone.slug.replace(/\//g, '').replace(/-/g, '_');

  return (
    <div className="min-h-screen" data-testid={`fz-page-${testIdBase}`}>
      <JsonLd
        id={`ld-${testIdBase}-service`}
        data={serviceSchema({
          name: zone.metaTitle.replace(' | WPAA', ''),
          description: zone.metaDescription,
          path: zone.slug,
          serviceType: `Free Zone Compliance Services - ${zone.name}`
        })}
      />
      <JsonLd
        id={`ld-${testIdBase}-breadcrumb`}
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'UAE Free Zone Services', path: '/free-zone-services-uae' },
          { name: zone.name, path: zone.slug }
        ])}
      />
      <JsonLd id={`ld-${testIdBase}-faq`} data={faqSchema(zone.faqs)} />

      {/* HERO */}
      <section className={`relative pt-32 pb-20 px-4 bg-gradient-to-br ${a.hero} text-white overflow-hidden`}>
        <div className="absolute inset-0 opacity-10">
          <div className={`absolute top-20 left-10 w-72 h-72 ${a.bg} rounded-full filter blur-3xl`}></div>
          <div className={`absolute bottom-10 right-10 w-96 h-96 ${a.bg} rounded-full filter blur-3xl`}></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <Building2 className={a.text} size={32} />
              <span className={`${a.text} font-semibold text-sm uppercase tracking-wider`}>{zone.heroEyebrow}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">{zone.h1}</h1>
            <p className="text-2xl text-white/90 mb-6 font-semibold">{zone.heroSubtitle}</p>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-3xl">{zone.heroBody}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={`/contact?service=free-zone-services&zone=${zone.name}`}>
                <Button size="lg" className={`${a.btnPrimary} text-white px-8 py-6 text-lg`} data-testid={`${testIdBase}-cta-consult`}>
                  Book Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid={`${testIdBase}-cta-wa`}>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  WhatsApp Us
                </Button>
              </a>
              <a href="tel:+971501219341" data-testid={`${testIdBase}-cta-call`}>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {(zone.layout || DEFAULT_LAYOUT).map((key, i) => {
        const Block = BLOCKS[key];
        return <Block key={key} zone={zone} a={a} bg={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} />;
      })}


      {/* CTA */}
      <section className={`py-20 px-4 bg-gradient-to-r ${a.ctaFrom} ${a.ctaTo} text-white`}>
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-4xl font-bold mb-6">{zone.ctaTitle || `Talk to a ${zone.name} Specialist`}</h2>
          <p className={`text-xl ${a.ctaSubText} mb-8 max-w-2xl mx-auto`}>
            {zone.ctaBody || `Book a free consultation and we'll review your ${zone.name} audit, VAT and Corporate Tax position in plain English.`}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Link to={`/contact?service=free-zone-services&zone=${zone.name}`}>
              <Button size="lg" className={`bg-white ${a.ctaText} hover:bg-gray-100 px-8 py-6 text-lg`} data-testid={`${testIdBase}-cta-bottom-consult`}>
                Schedule Consultation
              </Button>
            </Link>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid={`${testIdBase}-cta-bottom-wa`}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                WhatsApp Us
              </Button>
            </a>
          </div>
          <div className={`flex flex-col sm:flex-row justify-center items-center gap-6 ${a.ctaSubText}`}>
            <a href="tel:+971509987431" className="flex items-center gap-2 hover:text-white transition-colors" data-testid={`${testIdBase}-cta-phone`}>
              <Phone size={18} />
              <span>+971 50 998 7431</span>
            </a>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors" data-testid={`${testIdBase}-cta-whatsapp`}>
              <MessageSquare size={18} />
              <span>WhatsApp +971 50 121 9341</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreeZoneAuthorityPage;
